import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { ModalResponseCodes, ModalService } from '@src/suite/common/components/modal/modal.service';
import { Relationship } from '@src/suite/common/data-models/complex-types/relationship';
import { EnpsField } from '@src/suite/common/data-models/fields/enps-field';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { RecordModel } from '@src/suite/common/data-models/records/record';
import { NodeType } from '@src/suite/common/data-models/search-results/search-results';
import { ServiceResponse } from '@src/suite/common/data-models/service/service-response';
import { MAX_PARENT_TITLE_LENGTH, OPTION_BLANK } from '@src/suite/common/enums/constants';
import { DefaultAssignmentType } from '@src/suite/common/enums/enums';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { BasicContentService } from '@src/suite/common/services/basic-content-service';
import { Convertor } from '@src/suite/common/services/convertor';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { StoryEditorTextService } from '@src/suite/common/services/story-editor-text.service';
import { FullscreenActions, OpenFullscreenModalActions, selectAllStoriesMap, selectFullscreenNewRecord, selectFullscreenNewRecordDialogState } from '@src/suite/common/store/fullscreen';
import { selectMOSRecordSendFields } from '@src/suite/common/store/fullscreen/selectors/sidecar.selectors';
import { selectGroups, selectMosDevices, selectTitleSeparator } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { HelloResultsActions } from '@src/suite/common/store/hello-results/hello-results.actions';
import { selectHomeDatabase, selectHomeProgram } from '@src/suite/common/store/hello-results/hello-results.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { ListActions } from '@src/suite/common/store/list/list.actions';
import { MosActions } from '@src/suite/common/store/mos/actions/mos.actions';
import { ApplicationActions } from '@src/suite/common/store/navigation/actions/application.actions';
import { ViewStateActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import { selectCurrentViewState } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { NotesApiActions } from '@src/suite/common/store/notes/notes.actions';
import { AssignmentRecordActions, NotesRecordActions, RecordHttpActions, StoriesRecordActions, TopicRecordActions } from '@src/suite/common/store/records/records.actions';
import { selectAssignmentRecordsMap, selectStoryRecordsMap, selectStoryTemplatesMap, selectTopicRecordsMap } from '@src/suite/common/store/records/records.selectors';
import { StoryHttpActions, StoryMosActions } from '@src/suite/common/store/story/actions/story.actions';
import { I18nKeys } from '@src/suite/common/utils/i18n.default';
import { ncsItemRequestNoEditor, ncsStoryRequestResponse } from '@src/suite/common/utils/mos-utlis';
import { cleanupRecord, FIELDS_TO_SKIP_ON_CREATE, initializeRecord } from '@src/suite/common/utils/record-utils';
import { navigateTo } from '@src/suite/common/utils/store-utils';
import { getTextCommandsFromText } from '@src/suite/common/utils/util';
import { WireApiActions } from '@src/suite/discover/store/actions/discover.actions';
import { selectObjectPlaceholderToAssignmentType } from '@src/suite/live/store/selectors/live.selectors';
import { PlaybookService } from '@src/suite/playbook/services/playbook-service';
import { TopicService } from '@src/suite/playbook/services/topic-service';
import { DateTime } from 'luxon';
import { catchError, concatMap, exhaustMap, filter, from, map, of, switchMap } from 'rxjs';
import { v4 } from 'uuid';

import { RecordMosActions } from '../mos/actions/record-mos.actions';
import { PlaybookActions, PlaybookApiActions } from './playbook.actions';
import { selectTileViewDateRange, selectTreeControl } from './playbook.selector';

@Injectable()
export class PlaybookEffects {
    private actions$: Actions = inject(Actions);
    private playbookService: PlaybookService = inject(PlaybookService);
    private basicContentService: BasicContentService = inject(BasicContentService);
    private nomWebServicesJson: NomWebServicesJson = inject(NomWebServicesJson);
    private store: Store = inject(Store);
    private convertor = inject(Convertor);
    private modalService = inject(ModalService);
    private topicService = inject(TopicService);
    private storyEditorTextService = inject(StoryEditorTextService);

    createAssignment$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PlaybookApiActions.createAssignment),
            concatLatestFrom(() => {
                return [this.store.select(selectFullscreenNewRecord)];
            }),
            concatMap(([action, newRecord]) => {
                if (newRecord != null) {
                    // TODO: check this
                    const newAssignment = this.playbookService.generateNewAssignment(newRecord, action.assignmentType);
                    newAssignment.Guid = v4();
                    newAssignment.isNewRecord = true;
                    newAssignment.Title = newRecord.Title;
                    return of(FullscreenActions.addNewAssignmentToNewRecord({ assignment: newAssignment }));
                } else {
                    const path = `${action.topic.Path.fullPath}\\R_${action.topic.Guid}`;
                    const { Database } = action.topic;
                    return from(this.playbookService.createNewAssignmentV2(Database, path, action.assignmentType, action.topic)).pipe(
                        concatMap(res => {
                            const assignments = this.store.selectSignal(selectAssignmentRecordsMap)();
                            const topics = this.store.selectSignal(selectTopicRecordsMap)();
                            const topic = topics.get(action.topic?.Guid);

                            const actions = [];

                            if (topic.MOSControl) {
                                const objectPlaceholderToAssignmentType = this.store.selectSignal(selectObjectPlaceholderToAssignmentType)();
                                const placeholderType = objectPlaceholderToAssignmentType.get(res.data.PSTCoverageType?.id as DefaultAssignmentType);
                                if (placeholderType?.mosDeviceId && placeholderType?.mosObjectType) {
                                    actions.push(
                                        RecordHttpActions.saveRecordChanges({
                                            record: res.data,
                                            fieldsToUpdate: [
                                                {
                                                    key: PSTFieldNames.ObjectPlaceholderCreate,
                                                    value: `${placeholderType.mosDeviceId};${this.nomWebServicesJson.PSTGroup};${placeholderType.mosObjectType};${res.data.Title}`,
                                                },
                                            ],
                                        })
                                    );
                                }
                            }
                            const newMap = new Map(assignments);
                            res.data.ParentGuid = action.topic.Guid;
                            newMap.set(res.data.Guid, res.data);

                            // TODO: fix this in the createNewAssignmentV2 response
                            const updatedTopic = { ...topic };
                            if (!action.topic.Sequence?.includes(res.data.Guid)) {
                                updatedTopic.Sequence = [...(updatedTopic.Sequence || []), res.data.Guid];
                            }
                            // Add the new assignment to the topic's children collection
                            updatedTopic.children = [...(updatedTopic.children || []), res.data];

                            actions.push(
                                TopicRecordActions.updateTopics({ records: [updatedTopic] }),
                                AssignmentRecordActions.setAssignments({ records: newMap }),
                                RecordHttpActions.saveRecordChanges({ fieldsToUpdate: [{ key: PSTFieldNames.Type, value: action.topic.Type }], record: updatedTopic })
                            );
                            return actions;
                        })
                    );
                }
            })
        );
    });

    createStoryFromTemplate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PlaybookApiActions.createStoryFromTemplate),
            concatLatestFrom(() => [
                this.store.select(selectStoryTemplatesMap),
                this.store.select(selectStoryRecordsMap),
                this.store.select(selectTitleSeparator),
                this.store.select(selectTopicRecordsMap),
            ]),
            exhaustMap(([action, storyTemplatesMap, currentStoriesMap, titleSeparator, topicRecordsMap]) => {
                const { parentStoryGuid } = action;
                // we use parentStoryGuid to determine if the story is being created as a sub story,
                // // if it is, we will create the story as a new record and link it to the parent story,
                // otherwise we will create the story and add it to the topic.
                const asSubStory = !!parentStoryGuid;

                let parentStory: StorytellingStoryRecord | undefined;
                if (asSubStory) {
                    if (action?.topic?.isNewRecord) {
                        parentStory = (action.topic.stories || []).find(s => s.Guid === parentStoryGuid) as StorytellingStoryRecord;
                    } else {
                        parentStory = currentStoriesMap.get(parentStoryGuid);
                    }
                }

                // For sub-stories, derive title from parent story with sequential numbering
                let parentStoryTitle: string | undefined;
                let existingSubStoryCount = 0;
                if (asSubStory) {
                    parentStoryTitle = parentStory?.Title.trim();
                    existingSubStoryCount = parentStory?.PSTSubstoriesSequence || 0;
                }

                const truncatedParentTitle = parentStoryTitle && parentStoryTitle.length > MAX_PARENT_TITLE_LENGTH ? parentStoryTitle.substring(0, MAX_PARENT_TITLE_LENGTH) : parentStoryTitle;
                const parentTitleWasTruncated = asSubStory && !!parentStoryTitle && parentStoryTitle.length > MAX_PARENT_TITLE_LENGTH;

                const buildSubStoryTitle = (title: string, separator: string, seq: number): string => {
                    const truncated = title.length > MAX_PARENT_TITLE_LENGTH ? title.substring(0, MAX_PARENT_TITLE_LENGTH) : title;
                    return `${truncated}${separator}${seq}`;
                };

                if (action?.topic?.isNewRecord) {
                    const stories = [];
                    for (let i = 0; i < action.templateGuids.length; i++) {
                        const templateGuid = action.templateGuids[i];
                        const template = storyTemplatesMap.get(templateGuid);
                        const templateStory = initializeRecord<StorytellingStoryRecord>(ObjectTypes.SCRIPT, { ...template });
                        if (asSubStory) {
                            templateStory.PSTPrimaryStoryGUID = parentStoryGuid;
                        }
                        this.playbookService.initializeNewStoryRecordV2(templateStory as StorytellingStoryRecord, action.topic);
                        if (!template || template.Guid === OPTION_BLANK || !template.Title.trim()) {
                            templateStory.Title = action.topic.Title;
                        }
                        if (asSubStory && parentStoryTitle) {
                            templateStory.Title = buildSubStoryTitle(parentStoryTitle, titleSeparator, existingSubStoryCount + i + 1);
                        }
                        templateStory.FullStorySlug = templateStory.Title;
                        templateStory.Guid = v4();
                        templateStory.isNewRecord = true;
                        templateStory.isWriteable = RecordModel.globalTablesService.securityPrivileges[RecordModel.nomWebServicesJson.PSTGroup]?.story?.write;
                        //remove Slug and Segment if persists in the template story object.
                        delete templateStory.Slug;
                        delete templateStory.Segment;
                        if (asSubStory) {
                            templateStory.PSTSubstoriesSequence = existingSubStoryCount + i + 1;
                        }
                        stories.push(templateStory);
                    }
                    const resultActions: Action[] = [FullscreenActions.addNewStoriesToNewRecord({ stories })];
                    // Update parent story's PSTSubstoriesSequence and Title (if truncated)
                    if (asSubStory && parentStoryGuid && parentStory) {
                        const newSequence = existingSubStoryCount + action.templateGuids.length;
                        const updatedParent = {
                            ...parentStory,
                            PSTSubstoriesSequence: newSequence,
                            ...(parentTitleWasTruncated ? { Title: truncatedParentTitle, FullStorySlug: truncatedParentTitle } : {}),
                        };
                        const fieldsToUpdate: EnpsField[] = [{ key: PSTFieldNames.PSTSubstoriesSequence, value: newSequence }];
                        if (parentTitleWasTruncated) {
                            fieldsToUpdate.push({ key: PSTFieldNames.Title, value: truncatedParentTitle });
                        }
                        resultActions.push(StoriesRecordActions.updateStories({ records: [updatedParent] }), RecordHttpActions.saveRecordChanges({ record: updatedParent, fieldsToUpdate }));
                    }
                    return resultActions;
                } else {
                    let topic = action.topic;
                    if (!topic && asSubStory) {
                        const parentStory = currentStoriesMap.get(parentStoryGuid);
                        const topicGuid = parentStory?.PSTStoryTopicGUID;
                        if (topicGuid) {
                            topic = topicRecordsMap.get(topicGuid);
                        }
                    }
                    const updatedTopic = { ...topic };

                    const templateGuids = action.templateGuids.filter(guid => !!guid);
                    const subStoryTitles =
                        asSubStory && parentStoryTitle ? templateGuids.map((_, i) => buildSubStoryTitle(parentStoryTitle, titleSeparator, existingSubStoryCount + i + 1)) : undefined;
                    return from(
                        this.playbookService.createNewStoryFromTemplate({
                            database: updatedTopic.Database,
                            topic: updatedTopic,
                            templateProgramId: action.templateProgramId,
                            templateGuids,
                            parentStoryGuid: action.parentStoryGuid,
                            subStoryTitles,
                        })
                    ).pipe(
                        exhaustMap(res => {
                            if (res?.status) {
                                const newMap = new Map(currentStoriesMap);
                                for (const story of res.data) {
                                    newMap.set(story.Guid, story);
                                }
                                // Add the new stories to the topic's stories collection
                                updatedTopic.stories = [...(updatedTopic.stories || []), ...res.data];
                                const actions = [];
                                actions.push(
                                    ...[
                                        TopicRecordActions.updateTopics({ records: [updatedTopic] }),
                                        StoriesRecordActions.setStories({ records: newMap }),
                                        RecordHttpActions.saveRecordChanges({ fieldsToUpdate: [{ key: PSTFieldNames.Type, value: updatedTopic.Type }], record: updatedTopic }),
                                    ]
                                );

                                if (action.openStory && action.templateGuids.length === 1) {
                                    actions.push(FullscreenActions.openStory({ recordGuid: res.data[0].Guid, recordType: ObjectTypes.SCRIPT, fromPinnedItem: false, standaloneStory: false }));
                                }
                                if (!asSubStory) {
                                    actions.push(ListActions.addNewListGuids({ listGuids: [...res.data.map(story => story.Guid)] }));
                                }
                                // Update parent story's PSTSubstoriesSequence
                                if (asSubStory && parentStoryGuid) {
                                    const parentStory = currentStoriesMap.get(parentStoryGuid);
                                    if (parentStory) {
                                        const newSequence = existingSubStoryCount + res.data.length;
                                        const updatedParent = {
                                            ...parentStory,
                                            PSTSubstoriesSequence: newSequence,
                                            ...(parentTitleWasTruncated ? { Title: truncatedParentTitle, FullStorySlug: truncatedParentTitle } : {}),
                                        };
                                        const fieldsToUpdate: EnpsField[] = [{ key: PSTFieldNames.PSTSubstoriesSequence, value: newSequence }];
                                        if (parentTitleWasTruncated) {
                                            fieldsToUpdate.push({ key: PSTFieldNames.Title, value: truncatedParentTitle });
                                        }
                                        newMap.set(parentStoryGuid, updatedParent);
                                        actions.push(RecordHttpActions.saveRecordChanges({ record: updatedParent, fieldsToUpdate }));
                                    }
                                }
                                actions.push(FullscreenActions.setLoadingProgressBar({ showLoader: false }));
                                return actions;
                            }
                            return [ApplicationActions.noOpAction()];
                        })
                    );
                }
            })
        );
    });

    loadNoteTemplates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PlaybookApiActions.loadNoteTemplates),
            concatLatestFrom(() => this.store.select(selectCurrentViewState)),
            switchMap(([_, vs]) => {
                return this.playbookService.loadNoteTemplatesObservable(vs).pipe(
                    map(templates => NotesRecordActions.setNoteTemplates({ templates })),
                    catchError(error => of(PlaybookApiActions.loadTemplatesError({ error: error })))
                );
            })
        );
    });

    loadStoryTemplates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PlaybookApiActions.loadStoryTemplates, RecordHttpActions.loadPlaybookObjects),
            concatLatestFrom(() => this.store.select(selectCurrentViewState)),
            switchMap(([_, vs]) => {
                return this.playbookService.loadStoryTemplatesObservable(vs).pipe(
                    map(templates => StoriesRecordActions.setStoryTemplates({ templates })),
                    catchError(error => of(PlaybookApiActions.loadTemplatesError({ error: error })))
                );
            })
        );
    });

    loadTopicTemplates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PlaybookApiActions.loadTopicTemplates, RecordHttpActions.loadPlaybookObjects),
            concatLatestFrom(() => this.store.select(selectCurrentViewState)),
            switchMap(([_, vs]) => {
                return this.playbookService.loadTopicTemplatesObservable(vs).pipe(
                    map(templates => TopicRecordActions.setTopicTemplates({ templates })),
                    catchError(error => of(PlaybookApiActions.loadTemplatesError({ error: error })))
                );
            })
        );
    });

    navigateToTopic$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PlaybookActions.navigateToTopic),
            concatLatestFrom(() => this.store.select(selectTreeControl)),
            map(([action, treecontrol]) => {
                const candidates = treecontrol.dataNodes.map((item, index) => ({ item, index })).filter(({ item }) => item.nodeType === NodeType.RECORD);
                const nextItem = navigateTo(action.direction, action.currentNodeUuid, candidates);
                return ListActions.navigateObject({ nodeUuid: nextItem.uniqueId });
            })
        );
    });

    prepareCreateTopic$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PlaybookApiActions.prepareCreateTopic),
            concatLatestFrom(() => this.store.select(selectFullscreenNewRecordDialogState)),
            switchMap(([action, state]) => {
                if (state.wireRecord) {
                    return of(WireApiActions.createDiscoverItem({ topic: action.topic, wire: state.wireRecord, createAndNext: action.createAndNext }));
                }
                if (state.baseRecord?.Type === ObjectTypes.SCRIPT) {
                    const storiesMap = this.store.selectSignal(selectAllStoriesMap);
                    const storyRecord = storiesMap()?.get(state.baseRecord.Guid);
                    return of(StoryHttpActions.createTopicFromStory({ topic: action.topic, story: storyRecord, createAndOpen: action.createAndNext }));
                }
                return of(PlaybookApiActions.createTopic({ topic: action.topic, createAndNext: action.createAndNext }));
            })
        );
    });

    createTopic$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PlaybookApiActions.createTopic),
            concatLatestFrom(() => this.store.select(selectFullscreenNewRecordDialogState)),
            exhaustMap(([action, state]) => {
                let params: Record<string, any> = null;
                let topicToCreate = { ...action.topic };
                const topicStories = [...(topicToCreate.stories || [])]?.map(rec => cleanupRecord(rec)) || [];
                const topicAssignments = [...(topicToCreate.children || [])]?.map(rec => cleanupRecord(rec)) || [];
                if (action.discoverItemDatabase && action.discoverItemGuid) {
                    params = {};
                    params.discoverItemDatabase = action.discoverItemDatabase;
                    params.discoverItemPath = action.discoverItemPath.fullPath;
                    params.discoverItemGuid = action.discoverItemGuid;
                    topicToCreate.Relationships = new Relationship();
                    topicToCreate.Relationships.addRelationship(action.discoverItemGuid, ObjectTypes.DISCOVER_ITEM);
                }
                topicToCreate = cleanupRecord(topicToCreate);

                return from(this.playbookService.saveNewTopic(topicToCreate, topicAssignments, topicStories, params, state.isFromTemplate)).pipe(
                    exhaustMap(res => {
                        const actions = [];
                        actions.push(PlaybookApiActions.createRecordDone());
                        if (res.status) {
                            actions.push(TopicRecordActions.updateTopics({ records: [res.data] }));
                            if (res.data?.stories?.length) {
                                actions.push(StoriesRecordActions.updateStories({ records: res.data.stories }));
                            }

                            if (res.data?.children?.length) {
                                actions.push(AssignmentRecordActions.updateAssignments({ records: res.data.children }));
                            }

                            if (state.createInGroupData) {
                                actions.push(ListActions.addListGuids({ listGuids: [res.data.Guid] }));
                            } else {
                                actions.push(ListActions.addNewListGuids({ listGuids: [res.data.Guid] }));
                            }

                            actions.push(OpenFullscreenModalActions.closeFullscreenModalForce());
                            if (action?.createAndNext) {
                                actions.push(FullscreenActions.resetCurrentNewRecord({ recordType: state.record.Type }));
                                actions.push(OpenFullscreenModalActions.openTopicFullscreenDialog({ recordGuid: res.data.Guid, recordType: res.data.Type, isCreateAndNext: true }));
                            }
                        }
                        return actions;
                    })
                );
            }),
            catchError(() => of(PlaybookApiActions.createRecordDone()))
        );
    });

    createStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PlaybookApiActions.createStory),
            concatLatestFrom(() => [this.store.select(selectFullscreenNewRecordDialogState), this.store.select(selectHomeProgram)]),
            exhaustMap(([action, state, program]) => {
                try {
                    const isNewMOSRecord = action.story.isNewMOSRecord;
                    let storyToCreate = { ...action.story };
                    storyToCreate = cleanupRecord(storyToCreate);
                    const storyObject: any = {};
                    for (const p in storyToCreate) {
                        if (FIELDS_TO_SKIP_ON_CREATE.has(p.toUpperCase())) continue;
                        if (p.toUpperCase() === PSTFieldNames.TextCommands.toUpperCase()) continue;

                        const value = storyToCreate?.[p];
                        const enpsField = this.nomWebServicesJson.convertor.serializeField(value, p);
                        storyObject[enpsField.key] = enpsField.value;
                    }
                    const textCommands = getTextCommandsFromText(storyToCreate.text || storyToCreate.Text);
                    storyObject['TextCommandKeys'] = textCommands?.TextCommandKeys ?? [];
                    storyObject['TextCommandValues'] = textCommands?.TextCommandValues ?? [];

                    storyObject[PSTFieldNames.Actual] = -1;

                    return from(this.basicContentService.createBasicContent(this.nomWebServicesJson.PSTServer, `P_${program}\\W`, false, storyObject)).pipe(
                        exhaustMap(res => {
                            const actions = [];
                            actions.push(PlaybookApiActions.createRecordDone());
                            if (res.status) {
                                if (!action?.createAndNext) {
                                    if (!isNewMOSRecord && !action.keepFullscreenModal) {
                                        actions.push(OpenFullscreenModalActions.closeFullscreenModalForce());
                                    }
                                }

                                res.data.isNewRecord = false;
                                res.data.isNewMOSRecord = false;
                                actions.push(StoriesRecordActions.updateStories({ records: [res.data] }));

                                if (state.createInGroupData) {
                                    actions.push(ListActions.addListGuids({ listGuids: [res.data.Guid] }));
                                } else {
                                    actions.push(ListActions.addNewListGuids({ listGuids: [res.data.Guid] }));
                                }
                                if (isNewMOSRecord) {
                                    actions.push(FullscreenActions.pinItem({ itemGuid: res.data.Guid }));
                                    actions.push(FullscreenActions.openStory({ recordGuid: res.data.Guid, recordType: ObjectTypes.SCRIPT, fromPinnedItem: false, standaloneStory: true }));
                                }

                                if (action.createAndNext) {
                                    actions.push(FullscreenActions.resetCurrentNewRecord({ recordType: state.record.Type }));
                                    actions.push(FullscreenActions.openStory({ recordGuid: res.data.Guid, recordType: ObjectTypes.SCRIPT, fromPinnedItem: false, standaloneStory: true }));
                                }
                                actions.push(FullscreenActions.setLoadingProgressBar({ showLoader: false }));
                            }
                            return actions;
                        }),
                        catchError(() => of(PlaybookApiActions.createRecordDone()))
                    );
                } catch (error) {
                    console.error('Error in createStory$ effect', error);
                    return of(PlaybookApiActions.createRecordDone());
                }
            })
        );
    });

    createNote$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NotesApiActions.createNote),
            exhaustMap(({ note }) => {
                let noteToCreate = { ...note };
                noteToCreate = cleanupRecord(noteToCreate);
                const noteObject: Record<string, unknown> = {};
                for (const p in noteToCreate) {
                    if (FIELDS_TO_SKIP_ON_CREATE.has(p.toUpperCase())) continue;
                    const value = noteToCreate?.[p];
                    const enpsField = this.nomWebServicesJson.convertor.serializeField(value, p);
                    noteObject[enpsField.key] = enpsField.value;
                }
                return from(
                    this.basicContentService.createBasicContent(
                        this.nomWebServicesJson.PSTServer,
                        `P_${this.nomWebServicesJson.PSTGroup}\\W\\${this.nomWebServicesJson.PSTHoldFolder}`,
                        false,
                        noteObject
                    )
                ).pipe(
                    exhaustMap(res => {
                        return [NotesRecordActions.updateNotes({ records: [res.data] })];
                    })
                );
            })
        );
    });

    // TODO: delete this, not used.
    // IMPORTANT: this should not be used, it will be deleted
    saveStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PlaybookApiActions.saveStory),
            exhaustMap(action => {
                const fieldsToUpdate: EnpsField[] = [
                    { key: 'Text', value: action.story.text },
                    { key: PSTFieldNames.Type, value: ObjectTypes.SCRIPT },
                ];
                if (action.story.TextCommands) {
                    fieldsToUpdate.push({ key: PSTFieldNames.TextCommands, value: action.story.TextCommands });
                }
                return of(RecordHttpActions.saveRecordChanges({ record: action.story, fieldsToUpdate }));
            })
        );
    });

    /**
     * Handles a MOS `ncsStoryRequest` by building and sending the `ncsStoryRequestResponse`
     * XML back to the requesting plugin.
     *
     * ## Text freshness
     * The NgRx store's `text` field on a story record is only written on explicit save, so
     * it may be stale while the user is actively editing.  `StoryEditorTextService` is
     * queried for a live getter registered by the open `StoryFullScreenComponent`.  If a
     * getter exists the story record is cloned with the up-to-date text before being passed
     * to `ncsStoryRequestResponse`; otherwise the store value is used as-is (no open editor
     * means the stored text is the most recent).
     *
     * ## No-editor case
     * If `action.story` is absent (`ncsStoryRequest` arrived before any story was selected),
     * the effect replies with an `ncsAckResponse` ERROR so the plugin can surface a
     * "no editor" message to the operator.
     */
    mosRequestStoryRecord$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(StoryMosActions.requestMOSStoryRecord),
            concatLatestFrom(() => [this.store.select(selectGroups), this.store.select(selectHomeDatabase), this.store.select(selectMosDevices), this.store.select(selectMOSRecordSendFields)]),
            map(([action, groups, ncsID, mosDevices, sendFields]) => {
                const { story: record } = action;
                if (!record) {
                    return MosActions.mosClientCommandSend({
                        command: ncsItemRequestNoEditor(),
                        sidecar: action.sidecar,
                        mosPluginId: action.mosPluginId,
                    });
                }

                const mosDevice = mosDevices.find(device => device.ID === action.mosPluginId);
                const storyProgramDatabase = groups[record.Path?.groupId]?.Server;

                // Prefer live editor text over the (potentially stale) store value.
                // getLatestText() returns null when no editor is open for this story,
                // in which case the store record is used unchanged.
                const latestText = this.storyEditorTextService.getLatestText(record.Guid);
                const recordToSend = latestText != null ? { ...record, text: latestText } : record;
                return MosActions.mosClientCommandSend({
                    command: ncsStoryRequestResponse(recordToSend, storyProgramDatabase, ncsID, mosDevice, sendFields, this.convertor),
                    sidecar: action.sidecar,
                    mosPluginId: action.mosPluginId,
                });
            }),
            catchError(_ => {
                // @TODO Handle error
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    /*
    This is implemented specifically to handle the cases with RoverAI. And handling records like topic and Rundowns

    DELETE in 9.7: Eventually delete this in 9.7 when we move the roverAI to extensability.
    */
    mosRequestRecord$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordMosActions.requestMOSRecord),
            concatLatestFrom(() => [this.store.select(selectGroups), this.store.select(selectHomeDatabase), this.store.select(selectMosDevices), this.store.select(selectMOSRecordSendFields)]),
            map(([action, groups, ncsID, mosDevices, sendFields]) => {
                const { record } = action;
                if (!record) {
                    return MosActions.mosClientCommandSend({
                        command: ncsItemRequestNoEditor(),
                        sidecar: action.sidecar,
                        mosPluginId: action.mosPluginId,
                    });
                }

                const mosDevice = mosDevices.find(device => device.ID === action.mosPluginId);
                const recordProgramDatabase = groups[record.Path?.groupId]?.Server;
                return MosActions.mosClientCommandSend({
                    command: ncsStoryRequestResponse(record, recordProgramDatabase, ncsID, mosDevice, sendFields, this.convertor),
                    sidecar: action.sidecar,
                    mosPluginId: action.mosPluginId,
                });
            }),
            catchError(_ => {
                // @TODO Handle error
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    adjustDateRangeOnWeekDayChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(HelloResultsActions.setWeekDaySetting),
            switchMap(() => {
                const currentTileDateRange = this.store.selectSignal(selectTileViewDateRange)();
                const firstDate = DateTime.fromISO(currentTileDateRange.firstDate.toISO());
                const lastDate = DateTime.fromISO(currentTileDateRange.lastDate.toISO());
                return of(ViewStateActions.adjustDateRange({ firstDate, lastDate }));
            }),
            catchError(_ => {
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    mosControlActive$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PlaybookApiActions.mosControlActive),
            switchMap(action => {
                const i18n = this.store.selectSignal(selectI18N)();
                const topics = this.store.selectSignal(selectTopicRecordsMap)();
                const topic = topics.get(action.guid);

                if (topic?.MOSControl) {
                    return this.modalService
                        .openModal({
                            title: i18n[I18nKeys.PSTWebConfirmTurnOffMOSControlActive],
                            content: '',
                            yesString: i18n.PSTWebOkButtonLabel,
                            noString: i18n.PSTWebCancelButtonLabel,
                        })
                        .result.pipe(
                            filter(res => res === ModalResponseCodes.OK),
                            map(() => topic)
                        );
                }
                return of(topic);
            }),
            filter(topic => !!topic),
            switchMap(topic => {
                let dataToSend = {};
                const value = !topic.MOSControl;
                dataToSend = { ...dataToSend, MOSControl: value, AllowExternalMod: value, Type: ObjectTypes.TOPIC };

                return this.basicContentService
                    .updateBasicContentV2Obs({
                        database: topic.Database,
                        path: topic.Path.fullPath,
                        guid: topic.Guid,
                        keepApproved: false,
                        fields: dataToSend,
                        updateMessage: false,
                        oldRecord: topic,
                    })
                    .pipe(
                        filter((res: ServiceResponse<StorytellingTopicRecord>) => res.status),
                        switchMap(() => {
                            return this.topicService.getTopicContent(topic.Database, topic.Path.fullPath, topic.Guid);
                        })
                    );
            }),
            switchMap(res => {
                return of(TopicRecordActions.updateTopics({ records: [res.data] }));
            }),
            catchError(_ => of(ApplicationActions.noOpAction()))
        );
    });
}
